import { memo } from 'react'
import { Lock, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { itemHandlers, type PortfolioItemActions } from './item-actions'
import { BAR_HEIGHT, LANE_PITCH, LANE_TOP, type GridDensity, type PortfolioBar } from './layout'

interface PortfolioBarViewProps {
  bar: PortfolioBar
  propertyId: string
  actions: PortfolioItemActions
  density: GridDensity
  /**
   * Whether the label sticks at the property column's edge. Only a bar near
   * the left edge of the screen needs it; the grid asks for it there alone,
   * because the browser repositions every sticky element on every scroll frame
   * and hundreds of them cost frames (CAP-1416, measured).
   */
  sticky?: boolean
  /** Whether it takes the Tab key: only while it is in view. */
  focusable?: boolean
}

/**
 * One stay, block or orphan gap on a property row. A gap sits under the bars
 * (it is empty nights, and nothing else should be on them).
 *
 * The label is `position: sticky` at the property column's edge, so a bar that
 * started before the visible days keeps its label in view while it scrolls
 * (Uplisting's behaviour). The label is never wider than its bar and ends in an
 * ellipsis when it does not fit; the bar is clipped with `clip-path` rather
 * than `overflow: hidden`, which would make the bar its own scroll container
 * and stop the label sticking to the grid.
 *
 * A button (CAP-1418): a click, Enter or Space opens the item's sheet, the
 * property tab's own; hovering it or reaching it with the keyboard shows the
 * grid's tooltip, and its accessible name says the same in one line.
 */
export const PortfolioBarView = memo(function PortfolioBarView({
  bar,
  propertyId,
  actions,
  density,
  sticky = true,
  focusable = true,
}: PortfolioBarViewProps) {
  const { dayWidth, asideWidth, narrow } = density
  const left = bar.start * dayWidth + 1
  const width = Math.max(6, (bar.end - bar.start) * dayWidth - 2)
  return (
    <button
      type="button"
      data-testid={bar.testId}
      {...bar.data}
      {...itemHandlers(actions, propertyId, bar)}
      aria-label={bar.title}
      tabIndex={focusable ? undefined : -1}
      className={cn(
        'absolute flex cursor-pointer items-center rounded-md border p-0 text-left text-xs leading-5 [clip-path:inset(0_round_6px)]',
        // Inset: the bar is clipped to its rounded corners, which would cut an outer ring off.
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mint',
        bar.kind === 'gap' ? 'z-[1]' : 'z-[2]',
        bar.className
      )}
      style={{ left, width, height: BAR_HEIGHT, top: LANE_TOP + bar.lane * LANE_PITCH }}
    >
      <span
        data-testid="portfolio-bar-label"
        className={cn(
          'inline-flex min-w-0 max-w-full items-center gap-1 px-1.5 font-medium',
          sticky && 'sticky'
        )}
        style={sticky ? { left: asideWidth } : undefined}
      >
        {bar.kind === 'stay' && bar.stay.needs_reply === true && (
          // Only a known "yes": null is a thread that could not be read, and
          // is shown as neither answer.
          <MessageCircle
            data-testid="portfolio-needs-reply"
            className="h-3 w-3 shrink-0 text-amber"
            strokeWidth={2.5}
            // The bar's accessible name says "needs reply".
            aria-hidden="true"
          />
        )}
        {bar.kind === 'block' && bar.block.kind === 'owner' && (
          <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
        )}
        <span className="truncate">{narrow ? bar.shortLabel : bar.label}</span>
      </span>
    </button>
  )
})
